import { FC, useMemo, useState } from 'react';
import { Badge, Card, Col, Descriptions, Input, Modal, Row, Spin, Table, Tag } from 'antd';
import { useParams } from 'react-router-dom';
import * as Icons from '@ant-design/icons';
import { useCloseModal, useDebouncedValue } from '@shared/hooks';
import { useBranchInspectionById, useInspectionScans } from '../hooks';
import { IScan } from '../interfaces';

const formatDuration = (milliseconds: number) => {
  const totalSeconds = Math.floor(milliseconds / 1000);
  const totalMinutes = Math.floor(totalSeconds / 60);
  const totalHours = Math.floor(totalMinutes / 60);
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  const minutes = totalMinutes % 60;
  const seconds = totalSeconds % 60;

  if (days > 0) return `${days} gün ${hours} saat`;
  if (hours > 0) return `${hours} saat ${minutes} dəqiqə`;
  if (minutes > 0) return `${minutes} dəqiqə ${seconds} saniyə`;
  return `${seconds} saniyə`;
};

const getStatusColor = (stateId: number) => {
  if (stateId === 157) return 'success';
  if (stateId === 156) return 'processing';
  return 'default';
};

const getStatusIcon = (stateId: number) => {
  if (stateId === 157) return <Icons.CheckCircleOutlined />;
  if (stateId === 156) return <Icons.SyncOutlined spin />;
  return <Icons.ClockCircleOutlined />;
};

const getDeadlineStatus = (deadlineLeft: string, isCompleted: boolean) => {
  if (isCompleted) return 'success';
  if (deadlineLeft.includes('bitib')) return 'error';
  if (deadlineLeft.includes('saat') || deadlineLeft.includes('gün')) return 'warning';
  return 'success';
};

export const BranchInspectionDetails: FC = () => {
  const { id } = useParams<{ id: string }>();
  const [closeModal] = useCloseModal();
  const [searchText, setSearchText] = useState('');

  const { data, isFetching } = useBranchInspectionById(id);
  const { data: scans = [], isFetching: scansLoading } = useInspectionScans(id);

  const debouncedSearchText = useDebouncedValue(searchText, 300);

  const searchableScans = useMemo(() => scans.map((scan) => ({ scan, trackCode: (scan.trackCode?.toString() || '').toLowerCase(), barcode: (scan.barcode || '').toLowerCase() })), [scans]);

  const filteredScans = useMemo(() => {
    const formattedSearchText = debouncedSearchText.toLowerCase().trim();
    if (!formattedSearchText) return scans;
    return searchableScans.filter((entry) => entry.trackCode.includes(formattedSearchText) || entry.barcode.includes(formattedSearchText)).map((entry) => entry.scan);
  }, [scans, searchableScans, debouncedSearchText]);

  return (
    <Modal width={1200} open title="Filial yoxlanışı ətraflı məlumat" onCancel={() => closeModal('/branch-inspections')} footer={null}>
      <Spin spinning={isFetching}>
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={12}>
            <Card title="Ümumi məlumat" size="small">
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Yoxlanış №">
                  <strong>#{data?.id}</strong>
                </Descriptions.Item>
                <Descriptions.Item label="Filial">
                  <Tag color="blue" icon={<Icons.BankOutlined />}>
                    {data?.branch.name}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  <Badge status={getStatusColor(data?.stateId || 0) as any} text={data?.state?.name || '-'} />
                  <span style={{ marginLeft: 8 }}>{getStatusIcon(data?.stateId || 0)}</span>
                </Descriptions.Item>
                <Descriptions.Item label="Qeyd">{data?.note || '-'}</Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card title="Tarix məlumatları" size="small">
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Son tarix">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{data?.deadline.value}</span>
                    {data?.completedAt ? (
                      <Tag color="success" icon={<Icons.CheckCircleOutlined />}>
                        Tamamlanıb
                      </Tag>
                    ) : (
                      <Tag color={getDeadlineStatus(data?.deadline.left || '', false)} icon={<Icons.ClockCircleOutlined />}>
                        {data?.deadline.left}
                      </Tag>
                    )}
                  </div>
                </Descriptions.Item>
                <Descriptions.Item label="Başlanma tarixi">
                  {data?.startedAt ? (
                    <>
                      <Icons.PlayCircleOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                      {data.startedAt}
                    </>
                  ) : (
                    <Tag>Başlanmayıb</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Tamamlanma tarixi">
                  {data?.completedAt ? (
                    <>
                      <Icons.CheckCircleOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                      {data.completedAt}
                    </>
                  ) : (
                    <Tag>Tamamlanmayıb</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Yaradılma tarixi">
                  <Icons.CalendarOutlined style={{ marginRight: 8 }} />
                  {data?.createdAt}
                </Descriptions.Item>
                <Descriptions.Item label="Son yenilənmə">
                  <Icons.SyncOutlined style={{ marginRight: 8 }} />
                  {data?.updatedAt}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card title="İstifadəçi məlumatları" size="small">
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Yaradan">
                  <Tag color="purple" icon={<Icons.UserAddOutlined />}>
                    {data?.createdByUser.name}
                  </Tag>
                  <span style={{ marginLeft: 8, color: '#8c8c8c' }}>ID: {data?.createdByUser.id}</span>
                </Descriptions.Item>
                <Descriptions.Item label="İcraçı">
                  {data?.executedByUser ? (
                    <>
                      <Tag color="green" icon={<Icons.UserOutlined />}>
                        {data.executedByUser.name}
                      </Tag>
                      <span style={{ marginLeft: 8, color: '#8c8c8c' }}>ID: {data.executedByUser.id}</span>
                    </>
                  ) : (
                    <Tag>Təyin edilməyib</Tag>
                  )}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card title="Vaxt statistikası" size="small">
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <div style={{ padding: '0 8px' }}>
                    <div style={{ marginBottom: 8, color: '#8c8c8c', fontSize: 14 }}>
                      <Icons.FieldTimeOutlined style={{ marginRight: 8 }} />
                      İcraya başladı
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 600, color: '#262626' }}>
                      {data?.createdAt && data?.startedAt ? formatDuration(new Date(data.startedAt).getTime() - new Date(data.createdAt).getTime()) : '-'}
                    </div>
                    <div style={{ marginTop: 4, color: '#8c8c8c', fontSize: 12 }}>Yaradıldıqdan, ilk scana başlayana qədər</div>
                  </div>
                </Col>
                <Col xs={24} sm={12}>
                  <div style={{ padding: '0 8px' }}>
                    <div style={{ marginBottom: 8, color: '#8c8c8c', fontSize: 14 }}>
                      <Icons.HourglassOutlined style={{ marginRight: 8 }} />
                      İcra müddəti
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 600, color: '#3f8600' }}>
                      {data?.startedAt && data?.completedAt ? formatDuration(new Date(data.completedAt).getTime() - new Date(data.startedAt).getTime()) : '-'}
                    </div>
                    <div style={{ marginTop: 4, color: '#8c8c8c', fontSize: 12 }}>Başladıqdan tamamlanana kimi</div>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>

          <Col xs={24}>
            <Card title="Sistem identifikatorları" size="small">
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="Yoxlanış ID">{data?.id}</Descriptions.Item>
                <Descriptions.Item label="Filial ID">{data?.branchId}</Descriptions.Item>
                <Descriptions.Item label="Status ID">{data?.stateId}</Descriptions.Item>
                <Descriptions.Item label="Yaradan istifadəçi ID">{data?.createdBy}</Descriptions.Item>
                <Descriptions.Item label="İcraçı ID">{data?.executedBy || '-'}</Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>

          <Col xs={24}>
            <Card
              size="small"
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Scan edilmiş bağlamalar</span>
                  <Tag color="blue">{scans.length} ədəd</Tag>
                </div>
              }
            >
              <Input
                placeholder="İzləmə kodu və ya barkod ilə axtar..."
                prefix={<Icons.SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ marginBottom: 16 }}
                allowClear
              />
              <Table
                dataSource={filteredScans}
                rowKey="id"
                loading={scansLoading}
                pagination={{ pageSize: 50, showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} bağlama`, showSizeChanger: false }}
                size="small"
                scroll={{ x: 800 }}
              >
                <Table.Column title="№" key="index" width={60} render={(_, __, index) => index + 1} />
                <Table.Column
                  title="İzləmə kodu"
                  dataIndex="trackCode"
                  key="trackCode"
                  render={(text) => (
                    <Tag color="blue" style={{ fontFamily: 'monospace' }}>
                      {text}
                    </Tag>
                  )}
                />
                <Table.Column
                  title="Barkod"
                  dataIndex="barcode"
                  key="barcode"
                  render={(text, record: IScan) => (
                    <Tag color={record.trendyol && record.trendyol > 0 ? 'purple' : 'default'} style={{ fontFamily: 'monospace' }}>
                      {text || record.trackCode?.toString() || '-'}
                    </Tag>
                  )}
                />
                <Table.Column
                  title="Tip"
                  dataIndex="trendyol"
                  key="trendyol"
                  width={120}
                  render={(trendyol: number | null, record: IScan) => {
                    if (record.partnerId) return <Tag color="orange">Partner</Tag>;
                    if (trendyol === 1) return <Tag color="purple">Trendyol</Tag>;
                    if (trendyol === 2) return <Tag color="blue">Temu</Tag>;
                    return <Tag color="green">Findex</Tag>;
                  }}
                />
                <Table.Column title="Scan tarixi" dataIndex="scannedAt" key="scannedAt" width={180} />
                <Table.Column title="Bağlama ID" dataIndex="declarationId" key="declarationId" width={100} />
              </Table>
            </Card>
          </Col>
        </Row>
      </Spin>
    </Modal>
  );
};
